import React from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import axios from '../axiosClient.js';
import Alert from './common/Alert.jsx';
import PageLoading from './common/PageLoading.jsx';
import MonthlyNetSalesHeader from './MonthlyNetSalesHeader.jsx';
import MonthlyNetSalesRow from './MonthlyNetSalesRow.jsx';

const REFRESH_INTERVAL_MS = 300_000;

function MonthlyNetSales({ selectedYear, selectedRep, enabled = true }) {
  const [alertError, setAlertError] = React.useState(null);
  const [monthlyNetSales, setMonthlyNetSales] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Fetch explicitly on enable/selection; refresh only after the last request settles.
  React.useEffect(() => {
    const controller = new AbortController();
    let timer;
    setLoading(true);
    setAlertError(null);
    setMonthlyNetSales([]);

    const fetchSummary = async () => {
      try {
        const { data } = await axios.get(
          `/api/monthly-net-sales/${encodeURIComponent(selectedRep)}/${selectedYear}`,
          { signal: controller.signal, timeout: 60000 }
        );
        if (controller.signal.aborted) return;
        setMonthlyNetSales(data);
        setAlertError(null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setAlertError(err.response?.data?.error || err.message);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          timer = setTimeout(fetchSummary, REFRESH_INTERVAL_MS);
        }
      }
    };

    if (enabled && selectedRep && selectedYear) fetchSummary();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [selectedRep, selectedYear, enabled]);

  return (
    <Box>
      <Typography
        data-cy="monthly-net-sales-title-header"
        variant="h5"
        sx={{ textAlign: 'center' }}
      >
        Monthly Net Sales by Rep
      </Typography>
      <Alert
        data-cy="monthly-net-sales-alert"
        message={alertError}
        setMessage={setAlertError}
        level="error"
      />
      {loading ? (
        <PageLoading />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <MonthlyNetSalesHeader />
            </TableHead>
            <TableBody>
              {monthlyNetSales.length === 0 && (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    No category groups available for this selection.
                  </TableCell>
                </TableRow>
              )}
              {monthlyNetSales.map((netSale) => (
                <MonthlyNetSalesRow netSale={netSale} key={netSale.category} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default React.memo(MonthlyNetSales);
